// Web Crypto API is available globally in modern browsers
const crypto = window.crypto
import { v4 as uuidv4 } from 'uuid'
import ProviderAPIClient from '../ProviderAPIClient'

// Types for the session data
interface SessionData {
  fhirServer: string
  codeVerifier: string
  accessToken: string | null
  context: {
    fhirPatientId: string | null
    fhirUserId: string | null
    epicUserId: string | null
    epicUserFhirId: string | null
    userEmail: string | null
    userName: string | null
    userLastName: string | null
    providerNpi: string | null
    providerType: string | null
    loginDepartment: string | null
    epicVersion: string | null
    apiKey: string | null
  }
}

interface AccessResponse {
  message: string
  fhirPatientId: string
  fhirUserId: string
  epicUserId: string
  epicUserFhirId: string
  userEmail: string
  userName: string
  userLastName: string
  providerNpi: string
  providerType: string
  loginDepartment: string
  epicVersion: string
  apiKey: string
}

export class EpicAuthClient {
  private _sessions: Map<string, SessionData> = new Map()
  private static instance: EpicAuthClient | null = null
  private readonly SESSION_STORAGE_KEY = 'epic_fhir_sessions'
  private providerAPIClient: ProviderAPIClient

  constructor() {
    const requiredVars = ['VITE_EPIC_APP_CLIENT_ID', 'VITE_EPIC_LANDING_CALLBACK']

    for (const varName of requiredVars) {
      if (!import.meta.env[varName as keyof ImportMetaEnv]) {
        console.error(`Missing required environment variable: ${varName}`)
        throw new Error(`Missing required environment variable: ${varName}`)
      }
    }

    // Load sessions from localStorage on initialization
    this.loadSessionsFromStorage()
    this.providerAPIClient = ProviderAPIClient.getInstance()
  }

  /**
   * Get singleton instance of EpicAuthClient
   *
   * @returns EpicAuthClient instance
   */
  static getInstance(): EpicAuthClient {
    if (!EpicAuthClient.instance) {
      EpicAuthClient.instance = new EpicAuthClient()
    }
    return EpicAuthClient.instance
  }

  /**
   * Generate a code verifier for PKCE.
   * Uses cryptographically secure random bytes, encoded as URL-safe base64.
   *
   * @param length - Length of the verifier in bytes (default 64 gives 86 characters in base64)
   * @returns A random code verifier string
   */
  private async generateCodeVerifier(length: number = 64): Promise<string> {
    const array = new Uint8Array(length)
    crypto.getRandomValues(array)
    return btoa(String.fromCharCode(...array))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '')
  }

  /**
   * Generate a code challenge for PKCE using SHA256.
   *
   * @param codeVerifier - The code verifier to generate the challenge from
   * @returns The code challenge string
   */
  private async generateCodeChallenge(codeVerifier: string): Promise<string> {
    // Calculate SHA256 hash of the verifier
    const encoder = new TextEncoder()
    const data = encoder.encode(codeVerifier)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const hashString = String.fromCharCode(...hashArray)
    return btoa(hashString).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
  }

  /**
   * Generate authorization URL for OAuth2 flow and validate with Epic FHIR
   *
   * @param iss - Issuer URL
   * @param launch - Launch token
   * @returns Promise with redirect URL from Epic FHIR after validation
   */
  async retrieveSmartOnFHIRRedirectUrl(iss: string, launch: string): Promise<string> {
    // Generate PKCE values
    const codeVerifier = await this.generateCodeVerifier()
    const codeChallenge = await this.generateCodeChallenge(codeVerifier)
    const state = uuidv4()

    const fhirServer = iss.split('/api/FHIR/R4').at(0) || ''

    // Store the code verifier for later use in token exchange
    this.setSession(state, {
      fhirServer,
      codeVerifier,
      accessToken: null,
      context: {
        fhirPatientId: null,
        fhirUserId: null,
        epicUserId: null,
        epicUserFhirId: null,
        userEmail: null,
        userName: null,
        userLastName: null,
        providerNpi: null,
        providerType: null,
        loginDepartment: null,
        epicVersion: null,
        apiKey: null,
      },
    })

    const params = new URLSearchParams({
      code_challenge_method: 'S256',
      code_challenge: codeChallenge,
      client_id: import.meta.env.VITE_EPIC_APP_CLIENT_ID!,
      scope: 'launch openid fhirUser patient/*.read online_access',
      response_type: 'code',
      redirect_uri: import.meta.env.VITE_EPIC_LANDING_CALLBACK!,
      aud: iss,
      launch: launch,
      state,
      grant_type: 'authorization_code',
    })
    // Construct the URL with properly encoded parameters
    const url = `${fhirServer + '/oauth2/authorize'}?${params.toString()}`
    if (!url) {
      throw new Error('Failed to generate authorization URL')
    }
    return url
  }

  /**
   * Exchange authorization code for access token
   *
   * @param authorizationCode - The authorization code from the callback
   * @param state - The state parameter for session validation
   * @returns Access response with patient and user IDs
   */
  async retrieveSmartOnFHIRAccessContext(authorizationCode: string, state: string): Promise<AccessResponse> {
    if (!this._sessions.has(state)) {
      throw new Error('Invalid state parameter or session expired')
    }

    const session = this._sessions.get(state)!
    const codeVerifier = session.codeVerifier

    const requestBody = {
      authorizationCode: authorizationCode,
      redirectUri: import.meta.env.VITE_EPIC_LANDING_CALLBACK!,
      clientId: import.meta.env.VITE_EPIC_APP_CLIENT_ID!,
      codeVerifier: codeVerifier,
      fhirServer: session.fhirServer + '/oauth2/token',
    }

    const response = await this.providerAPIClient.post(
      '/EpicFhirAccessToken',
      requestBody,
      {
        'Content-Type': 'application/json',
      },
      false
    )

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(`API Gateway request failed: ${response.status} - ${errorData.message || response.statusText}`)
    }
    const contents = (await response.json()) as AccessResponse & { access_token?: string }

    // Validate required fields
    if (!contents.access_token || !contents.apiKey) {
      throw new Error('API Gateway response missing required fields: access_token and/or apiKey')
    }

    // Update session with access token and context
    session.accessToken = contents.access_token

    session.context.fhirPatientId = contents.fhirPatientId
    session.context.fhirUserId = contents.fhirUserId
    session.context.epicUserId = contents.epicUserId
    session.context.epicUserFhirId = contents.epicUserFhirId
    session.context.userEmail = contents.userEmail
    session.context.userName = contents.userName
    session.context.userLastName = contents.userLastName
    session.context.providerNpi = contents.providerNpi
    session.context.providerType = contents.providerType
    session.context.loginDepartment = contents.loginDepartment
    session.context.epicVersion = contents.epicVersion
    session.context.apiKey = contents.apiKey

    this.providerAPIClient.setApiKey(contents.apiKey)

    // Save updated session to storage
    this.saveSessionsToStorage()

    return {
      message: 'Successfully retrieved access token',
      fhirPatientId: contents.fhirPatientId,
      fhirUserId: contents.fhirUserId,
      epicUserId: contents.epicUserId,
      epicUserFhirId: contents.epicUserFhirId,
      userEmail: contents.userEmail,
      userName: contents.userName,
      userLastName: contents.userLastName,
      providerNpi: contents.providerNpi,
      providerType: contents.providerType,
      loginDepartment: contents.loginDepartment,
      epicVersion: contents.epicVersion,
      apiKey: contents.apiKey,
    }
  }

  /**
   * Get access token for a given state
   *
   * @param state - The state parameter
   * @returns Access token or null if not found
   */
  getAccessToken(state: string): string | null {
    const session = this._sessions.get(state)
    return session?.accessToken || null
  }

  getIssuerUrl(state: string): string {
    const session = this._sessions.get(state)
    return session?.fhirServer || ''
  }

  /**
   * Get context for a given state
   *
   * @param state - The state parameter
   * @returns Context object with patient and user IDs
   */
  getContext(state: string): {
    fhirPatientId: string | null
    fhirUserId: string | null
    epicUserId: string | null
    epicUserFhirId: string | null
    userEmail: string | null
    userName: string | null
    userLastName: string | null
    providerNpi: string | null
    providerType: string | null
    loginDepartment: string | null
    epicVersion: string | null
    apiKey: string | null
  } {
    const session = this._sessions.get(state)
    return (
      session?.context || {
        fhirPatientId: null,
        fhirUserId: null,
        epicUserId: null,
        epicUserFhirId: null,
        userEmail: null,
        userName: null,
        userLastName: null,
        providerNpi: null,
        providerType: null,
        loginDepartment: null,
        epicVersion: null,
        apiKey: null,
      }
    )
  }

  /**
   * Check if a session exists for a given state
   *
   * @param state - The state parameter
   * @returns True if session exists, false otherwise
   */
  hasSession(state: string): boolean {
    return this._sessions.has(state)
  }
  /**
   * Get all active session states (useful for debugging)
   *
   * @returns Array of active session states
   */
  getActiveSessions(): string[] {
    return Array.from(this._sessions.keys())
  }

  /**
   * Load sessions from localStorage
   */
  private loadSessionsFromStorage(): void {
    try {
      const storedSessions = localStorage.getItem(this.SESSION_STORAGE_KEY)
      if (storedSessions) {
        const sessionsData = JSON.parse(storedSessions)
        this._sessions = new Map(Object.entries(sessionsData))
      }
    } catch (error) {
      console.warn('Failed to load sessions from localStorage:', error)
    }
  }

  /**
   * Save sessions to localStorage
   */
  private saveSessionsToStorage(): void {
    try {
      const sessionsData = Object.fromEntries(this._sessions)
      localStorage.setItem(this.SESSION_STORAGE_KEY, JSON.stringify(sessionsData))
    } catch (error) {
      console.warn('Failed to save sessions to localStorage:', error)
    }
  }

  /**
   * Set session with persistence
   */
  private setSession(state: string, sessionData: SessionData): void {
    this._sessions.set(state, sessionData)
    this.saveSessionsToStorage()
  }

  /**
   * Remove session with persistence
   */
  removeSession(state: string): void {
    this._sessions.delete(state)
    this.saveSessionsToStorage()
  }

  /**
   * Clear all sessions (useful for logout)
   */
  clearAllSessions(): void {
    this._sessions.clear()
    localStorage.removeItem(this.SESSION_STORAGE_KEY)
  }

  /**
   * Clean up expired sessions (optional - can be called periodically)
   */
  cleanupExpiredSessions(): void {
    const expiredStates: string[] = []

    // Add expiration logic here if needed
    // For now, we'll keep all sessions

    expiredStates.forEach((state) => this.removeSession(state))
  }
}
